import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Trash2, Download, Settings as SettingsIcon, Users, Send, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import WysiwygEditor from "@/components/WysiwygEditor";

interface NewsletterSettings {
  id: string;
  welcome_subject: string;
  welcome_message: string;
  sender_name: string;
  sender_email: string;
  is_enabled: boolean;
}

interface Subscriber {
  id: string;
  email: string;
  subscribed_at: string;
  status: string;
}

interface Campaign {
  id: string;
  subject: string;
  content: string;
  sent_at: string;
  recipient_count: number;
}

interface PromoCode {
  id: string;
  code: string;
  description: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  currency: string;
  end_date: string | null;
  min_purchase_amount: number;
}

interface Advertisement {
  id: string;
  title: string;
  description: string;
  image_url: string | null;
  link_url: string | null;
}

export const NewsletterManager = () => {
  const [settings, setSettings] = useState<NewsletterSettings | null>(null);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [advertisements, setAdvertisements] = useState<Advertisement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [campaignSubject, setCampaignSubject] = useState("");
  const [campaignContent, setCampaignContent] = useState("");
  const [selectedPromoCode, setSelectedPromoCode] = useState<string>("");
  const [selectedAdvertisement, setSelectedAdvertisement] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiTone, setAiTone] = useState<"professional" | "casual" | "enthusiastic" | "informative">("professional");
  const [aiLength, setAiLength] = useState<"short" | "medium" | "long">("medium");
  const [showAiDialog, setShowAiDialog] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchSubscribers();
    fetchCampaigns();
    fetchPromoCodes();
    fetchAdvertisements();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      const { data, error } = await supabase
        .from('promo_codes')
        .select('id, code, description, discount_type, discount_value, end_date, min_purchase_amount')
        .eq('active', true)
        .order('code');

      if (error) throw error;
      // Map data to include default currency if not present
      const mappedData = (data || []).map(item => ({
        ...item,
        currency: 'XOF'
      }));
      setPromoCodes(mappedData as PromoCode[]);
    } catch (error: any) {
      console.error('Error fetching promo codes:', error);
    }
  };

  const fetchAdvertisements = async () => {
    try {
      const { data, error } = await supabase
        .from('advertisements')
        .select('id, title, image_url, link_url')
        .eq('active', true)
        .gte('end_date', new Date().toISOString())
        .order('title');

      if (error) throw error;
      // Map data to include empty description if not present
      const mappedData = (data || []).map(item => ({
        ...item,
        description: ''
      }));
      setAdvertisements(mappedData as Advertisement[]);
    } catch (error: any) {
      console.error('Error fetching advertisements:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("newsletter_settings")
        .select("*")
        .single();

      if (error) throw error;
      setSettings(data);
    } catch (error: any) {
      toast.error("Erreur lors du chargement des paramètres");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const { data, error } = await supabase
        .from("newsletter_subscribers")
        .select("*")
        .order("subscribed_at", { ascending: false });

      if (error) throw error;
      setSubscribers(data || []);
    } catch (error: any) {
      toast.error("Erreur lors du chargement des abonnés");
      console.error(error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const { data, error } = await supabase
        .from("newsletter_campaigns")
        .select("*")
        .order("sent_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      setCampaigns(data || []);
    } catch (error: any) {
      toast.error("Erreur lors du chargement des campagnes");
      console.error(error);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("newsletter_settings")
        .update({
          welcome_subject: settings.welcome_subject,
          welcome_message: settings.welcome_message,
          sender_name: settings.sender_name,
          sender_email: settings.sender_email,
          is_enabled: settings.is_enabled,
        })
        .eq("id", settings.id);

      if (error) throw error;
      toast.success("Paramètres enregistrés avec succès");
    } catch (error: any) {
      toast.error("Erreur lors de l'enregistrement");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleSubscriberStatus = async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === "active" ? "unsubscribed" : "active";
    const action = newStatus === "active" ? "réinscrire" : "désinscrire";
    
    if (!confirm(`Êtes-vous sûr de vouloir ${action} cet abonné ?`)) return;

    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .update({ status: newStatus })
        .eq("id", id);

      if (error) throw error;
      toast.success(`Abonné ${newStatus === "active" ? "réinscrit" : "désinscrit"} avec succès`);
      fetchSubscribers();
    } catch (error: any) {
      toast.error(`Erreur lors de la modification du statut`);
      console.error(error);
    }
  };

  const handleDeleteSubscriber = async (id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer définitivement cet abonné ?")) return;

    try {
      const { error } = await supabase
        .from("newsletter_subscribers")
        .delete()
        .eq("id", id);

      if (error) throw error;
      toast.success("Abonné supprimé");
      fetchSubscribers();
    } catch (error: any) {
      toast.error("Erreur lors de la suppression");
      console.error(error);
    }
  };

  const handleExportSubscribers = () => {
    const csv = [
      ["Email", "Date d'inscription", "Statut"],
      ...subscribers.map(s => [
        s.email,
        format(new Date(s.subscribed_at), "dd/MM/yyyy HH:mm", { locale: fr }),
        s.status
      ])
    ].map(row => row.join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `newsletter-subscribers-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleGenerateContent = async () => {
    if (!aiTopic.trim()) {
      toast.error("Veuillez entrer un sujet");
      return;
    }

    setIsGenerating(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Session expirée, veuillez vous reconnecter");
        setIsGenerating(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('generate-newsletter-content', {
        body: {
          topic: aiTopic,
          tone: aiTone,
          length: aiLength,
          includePromoCode: !!selectedPromoCode,
          includeAdvertisement: !!selectedAdvertisement,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Erreur lors de la génération:', error);
        throw error;
      }

      setCampaignContent(data.content);
      setCampaignSubject(data.subject);
      
      setShowAiDialog(false);
      toast.success("Contenu généré avec succès !");
    } catch (error: any) {
      console.error('Erreur lors de la génération:', error);
      toast.error(error.message || "Erreur lors de la génération du contenu");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendCampaign = async () => {
    if (!campaignSubject || !campaignContent) {
      toast.error("Le sujet et le contenu sont requis");
      return;
    }

    if (!confirm(`Êtes-vous sûr de vouloir envoyer cette campagne à ${subscribers.filter(s => s.status === "active").length} abonnés ?`)) {
      return;
    }

    setIsSending(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-newsletter-campaign", {
        body: {
          subject: campaignSubject,
          content: campaignContent,
          promoCodeId: selectedPromoCode || null,
          advertisementId: selectedAdvertisement || null,
        },
      });

      if (error) throw error;

      toast.success(data.message || "Campagne envoyée avec succès");
      setCampaignSubject("");
      setCampaignContent("");
      setSelectedPromoCode("");
      setSelectedAdvertisement("");
      fetchCampaigns();
    } catch (error: any) {
      toast.error(error.message || "Erreur lors de l'envoi de la campagne");
      console.error(error);
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Gestion de la Newsletter</CardTitle>
        <CardDescription>
          Configurez votre newsletter et gérez vos abonnés
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="subscribers" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="subscribers" className="gap-2">
              <Users className="h-4 w-4" />
              Abonnés ({subscribers.filter(s => s.status === "active").length})
            </TabsTrigger>
            <TabsTrigger value="campaigns" className="gap-2">
              <Send className="h-4 w-4" />
              Campagnes
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <SettingsIcon className="h-4 w-4" />
              Paramètres
            </TabsTrigger>
          </TabsList>

          <TabsContent value="subscribers" className="space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Total: {subscribers.length} abonnés
              </p>
              <Button onClick={handleExportSubscribers} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Exporter CSV
              </Button>
            </div>

            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Date d'inscription</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscribers.map((subscriber) => (
                    <TableRow key={subscriber.id}>
                      <TableCell className="font-medium">{subscriber.email}</TableCell>
                      <TableCell>
                        {format(new Date(subscriber.subscribed_at), "dd/MM/yyyy HH:mm", { locale: fr })}
                      </TableCell>
                      <TableCell>
                        <Badge variant={subscriber.status === "active" ? "default" : "secondary"}>
                          {subscriber.status === "active" ? "Actif" : "Désinscrit"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleSubscriberStatus(subscriber.id, subscriber.status)}
                            title={subscriber.status === "active" ? "Désinscrire" : "Réinscrire"}
                          >
                            {subscriber.status === "active" ? (
                              <span className="text-orange-600">✕</span>
                            ) : (
                              <span className="text-green-600">✓</span>
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteSubscriber(subscriber.id)}
                            title="Supprimer définitivement"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {subscribers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        Aucun abonné pour le moment
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="campaigns" className="space-y-4">
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => setShowAiDialog(true)}
                variant="outline"
                size="sm"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Générer avec l'IA
              </Button>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="campaign_subject">Sujet de la campagne</Label>
                <Input
                  id="campaign_subject"
                  value={campaignSubject}
                  onChange={(e) => setCampaignSubject(e.target.value)}
                  placeholder="Ex: Nos nouveautés de la semaine"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="campaign_content">Contenu du mail</Label>
                <WysiwygEditor
                  value={campaignContent}
                  onChange={setCampaignContent}
                  placeholder="Rédigez votre message ici..."
                  className="bg-background"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="promo_code">Code promo (optionnel)</Label>
                  <select
                    id="promo_code"
                    value={selectedPromoCode}
                    onChange={(e) => setSelectedPromoCode(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                  >
                    <option value="">Aucun code promo</option>
                    {promoCodes.map((promo) => (
                      <option key={promo.id} value={promo.id}>
                        {promo.code} - {promo.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="advertisement">Publicité (optionnel)</Label>
                  <select
                    id="advertisement"
                    value={selectedAdvertisement}
                    onChange={(e) => setSelectedAdvertisement(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                  >
                    <option value="">Aucune publicité</option>
                    {advertisements.map((ad) => (
                      <option key={ad.id} value={ad.id}>
                        {ad.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <Button 
                onClick={handleSendCampaign} 
                disabled={isSending || !campaignSubject || !campaignContent}
                className="w-full"
              >
                {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                <Send className="mr-2 h-4 w-4" />
                Envoyer la campagne à {subscribers.filter(s => s.status === "active").length} abonnés
              </Button>
            </div>

            <div className="border-t pt-4 mt-6">
              <h3 className="text-lg font-semibold mb-4">Historique des campagnes</h3>
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sujet</TableHead>
                      <TableHead>Date d'envoi</TableHead>
                      <TableHead>Destinataires</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {campaigns.map((campaign) => (
                      <TableRow key={campaign.id}>
                        <TableCell className="font-medium">{campaign.subject}</TableCell>
                        <TableCell>
                          {format(new Date(campaign.sent_at), "dd/MM/yyyy HH:mm", { locale: fr })}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{campaign.recipient_count}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {campaigns.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-muted-foreground">
                          Aucune campagne envoyée pour le moment
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="settings" className="space-y-4">
            {settings && (
              <>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label htmlFor="is_enabled" className="text-base">Newsletter active</Label>
                    <p className="text-sm text-muted-foreground">
                      Activer ou désactiver les inscriptions à la newsletter
                    </p>
                  </div>
                  <Switch
                    id="is_enabled"
                    checked={settings.is_enabled}
                    onCheckedChange={(checked) =>
                      setSettings({ ...settings, is_enabled: checked })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sender_name">Nom de l'expéditeur</Label>
                  <Input
                    id="sender_name"
                    value={settings.sender_name}
                    onChange={(e) =>
                      setSettings({ ...settings, sender_name: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sender_email">Email de l'expéditeur</Label>
                  <Input
                    id="sender_email"
                    type="email"
                    value={settings.sender_email}
                    onChange={(e) =>
                      setSettings({ ...settings, sender_email: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="welcome_subject">Sujet de l'email de bienvenue</Label>
                  <Input
                    id="welcome_subject"
                    value={settings.welcome_subject}
                    onChange={(e) =>
                      setSettings({ ...settings, welcome_subject: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="welcome_message">Message de bienvenue</Label>
                  <WysiwygEditor
                    value={settings.welcome_message}
                    onChange={(value) =>
                      setSettings({ ...settings, welcome_message: value })
                    }
                    placeholder="Message de bienvenue pour les nouveaux abonnés..."
                    className="bg-background"
                  />
                </div>

                <Button onClick={handleSaveSettings} disabled={isSaving}>
                  {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Enregistrer les paramètres
                </Button>
              </>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
      
      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Générer un contenu avec l'IA</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ai-topic">Sujet de la newsletter *</Label>
              <Input
                id="ai-topic"
                placeholder="Ex: Nos nouveautés de l'été, Conseils pour économiser..."
                value={aiTopic}
                onChange={(e) => setAiTopic(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-tone">Ton</Label>
              <Select value={aiTone} onValueChange={(value: any) => setAiTone(value)}>
                <SelectTrigger id="ai-tone">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professionnel</SelectItem>
                  <SelectItem value="casual">Décontracté</SelectItem>
                  <SelectItem value="enthusiastic">Enthousiaste</SelectItem>
                  <SelectItem value="informative">Informatif</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-length">Longueur</Label>
              <Select value={aiLength} onValueChange={(value: any) => setAiLength(value)}>
                <SelectTrigger id="ai-length">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="short">Court (150-200 mots)</SelectItem>
                  <SelectItem value="medium">Moyen (250-350 mots)</SelectItem>
                  <SelectItem value="long">Long (400-500 mots)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-promo">Code promo à mentionner (optionnel)</Label>
              <Select value={selectedPromoCode} onValueChange={setSelectedPromoCode}>
                <SelectTrigger id="ai-promo">
                  <SelectValue placeholder="Sélectionner un code promo" />
                </SelectTrigger>
                <SelectContent>
                  {promoCodes.map((promo) => (
                    <SelectItem key={promo.id} value={promo.id}>
                      {promo.code} - {promo.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ai-ad">Publicité à mentionner (optionnel)</Label>
              <Select value={selectedAdvertisement} onValueChange={setSelectedAdvertisement}>
                <SelectTrigger id="ai-ad">
                  <SelectValue placeholder="Sélectionner une publicité" />
                </SelectTrigger>
                <SelectContent>
                  {advertisements.map((ad) => (
                    <SelectItem key={ad.id} value={ad.id}>
                      {ad.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAiDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleGenerateContent} disabled={isGenerating || !aiTopic.trim()}>
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Génération...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Générer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
